import type { SavedDesign } from '../schemas/design.js'

/**
 * The stock design document every new agency (and the public trial page)
 * starts from. GENERATED — replace by running the export script against an
 * agency whose design was authored in the Customize editor:
 *
 *   pnpm --filter @resumeheaders/server export:default-design <agencyId>
 *
 * Authoring rules (see default-design.ts for the machinery):
 * - Use `{{agency.*}}` / `{{recruiter.*}}` tokens in text blocks for every
 *   field the trial form / onboarding collects — values bind at render
 *   time, and lines whose tokens all resolve empty are collapsed.
 * - Use DEFAULT_DESIGN_BRAND_COLOR (#52547a) anywhere the user's brand
 *   color should apply; it is substituted at trial render / agency creation.
 * - No image components: assetKeys are agency-scoped and won't resolve for
 *   other agencies.
 */
export const DEFAULT_DESIGN_DATA: SavedDesign = {
  version: 1,
  pageSize: 'letter',
  pageMode: 'all',
  firstPage: {
    components: [
      {
        id: 'resume-4',
        x: 0,
        y: 75,
        width: 799,
        height: 969,
        rotation: 0,
        type: 'resume',
      },
      {
        id: 'header-band-1',
        x: 17,
        y: 0,
        width: 799,
        height: 102,
        rotation: 0,
        type: 'container',
        backgroundColor: 'rgba(82,84,122,0.15)',
        borderLinked: true,
        borderTop: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderRight: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderBottom: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderLeft: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        radiusLinked: true,
        radiusTL: 0,
        radiusTR: 0,
        radiusBR: 0,
        radiusBL: 0,
        padding: 0,
      },
      {
        id: 'text-2',
        x: 35,
        y: 1,
        width: 221,
        height: 100,
        rotation: 0,
        type: 'text',
        content:
          '<font style="font-size: 20px" color="#52547a"><b style="">{{agency.name}}</b></font><div><font style="font-size: 16px"><b>{{agency.email}}</b></font></div><div><span style="font-size: 16px"><b>{{agency.phone}}</b></span></div><div><span style="font-size: 16px"><b>{{agency.linkedIn}}</b></span></div>',
        fontSize: 12,
        fontFamily: 'Arial, sans-serif',
        textAlign: 'left',
        lineHeight: 1.3,
        color: '#000000',
      },
      {
        id: 'text-3',
        x: 495,
        y: 4,
        width: 306,
        height: 102,
        rotation: 0,
        type: 'text',
        content:
          '<div style="text-align: right"><b style="font-size: 16px">{{recruiter.name}}</b></div><div style="text-align: right"><b style="font-size: 16px">{{recruiter.phone}}</b></div><font style="font-size: 16px"><div style="text-align: right"><b>{{recruiter.email}}</b></div><div style="text-align: right"><b>{{recruiter.linkedIn}}</b></div></font>',
        fontSize: 12,
        fontFamily: 'Arial, sans-serif',
        textAlign: 'left',
        lineHeight: 1.3,
        color: '#000000',
      },
      {
        x: 153,
        y: 425,
        width: 500,
        height: 84,
        rotation: -45,
        content:
          '<font style="color: rgba(220, 38, 38, 0.37); font-size: 60px">CONFIDENTIAL</font>',
        fontSize: 52,
        fontFamily: 'Arial, sans-serif',
        textAlign: 'center',
        lineHeight: 1.2,
        color: 'rgba(220,38,38,0.3)',
        type: 'watermark',
        id: 'watermark-singleton',
      },
      {
        id: 'container-1784331976934-1',
        x: 15,
        y: 1023,
        width: 799,
        height: 33,
        rotation: 0,
        type: 'container',
        backgroundColor: 'rgba(82,84,122,0.14)',
        borderLinked: true,
        borderTop: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderRight: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderBottom: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderLeft: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        radiusLinked: true,
        radiusTL: 4,
        radiusTR: 4,
        radiusBR: 4,
        radiusBL: 4,
        padding: 8,
      },
      {
        id: 'text-1784331986039-2',
        x: 292,
        y: 1026,
        width: 200,
        height: 30,
        rotation: 0,
        type: 'text',
        content: '<div style="text-align: center">{{agency.url}}</div>',
        fontSize: 16,
        fontFamily: 'Arial, sans-serif',
        textAlign: 'left',
        lineHeight: 1.25,
        color: '#333333',
      },
      {
        id: 'container-1784664665313-1',
        x: 0,
        y: 0,
        width: 20,
        height: 1056,
        rotation: 0,
        type: 'container',
        backgroundColor: '#52547a',
        borderLinked: true,
        borderTop: {
          width: 1,
          style: 'solid',
          color: '#52547a',
        },
        borderRight: {
          width: 1,
          style: 'solid',
          color: '#52547a',
        },
        borderBottom: {
          width: 1,
          style: 'solid',
          color: '#52547a',
        },
        borderLeft: {
          width: 1,
          style: 'solid',
          color: '#52547a',
        },
        radiusLinked: true,
        radiusTL: 0,
        radiusTR: 0,
        radiusBR: 0,
        radiusBL: 0,
        padding: 8,
      },
    ],
  },
  subsequentPages: {
    components: [
      {
        id: 'resume-4',
        x: 0,
        y: 75,
        width: 799,
        height: 969,
        rotation: 0,
        type: 'resume',
      },
      {
        id: 'header-band-1',
        x: 17,
        y: 0,
        width: 799,
        height: 102,
        rotation: 0,
        type: 'container',
        backgroundColor: 'rgba(82,84,122,0.15)',
        borderLinked: true,
        borderTop: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderRight: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderBottom: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderLeft: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        radiusLinked: true,
        radiusTL: 0,
        radiusTR: 0,
        radiusBR: 0,
        radiusBL: 0,
        padding: 0,
      },
      {
        id: 'text-2',
        x: 35,
        y: 1,
        width: 221,
        height: 100,
        rotation: 0,
        type: 'text',
        content:
          '<font style="font-size: 20px" color="#52547a"><b style="">{{agency.name}}</b></font><div><font style="font-size: 16px"><b>{{agency.email}}</b></font></div><div><span style="font-size: 16px"><b>{{agency.phone}}</b></span></div><div><span style="font-size: 16px"><b>{{agency.linkedIn}}</b></span></div>',
        fontSize: 12,
        fontFamily: 'Arial, sans-serif',
        textAlign: 'left',
        lineHeight: 1.3,
        color: '#000000',
      },
      {
        id: 'text-3',
        x: 495,
        y: 4,
        width: 306,
        height: 102,
        rotation: 0,
        type: 'text',
        content:
          '<div style="text-align: right"><b style="font-size: 16px">{{recruiter.name}}</b></div><div style="text-align: right"><b style="font-size: 16px">{{recruiter.phone}}</b></div><font style="font-size: 16px"><div style="text-align: right"><b>{{recruiter.email}}</b></div><div style="text-align: right"><b>{{recruiter.linkedIn}}</b></div></font>',
        fontSize: 12,
        fontFamily: 'Arial, sans-serif',
        textAlign: 'left',
        lineHeight: 1.3,
        color: '#000000',
      },
      {
        x: 153,
        y: 425,
        width: 500,
        height: 84,
        rotation: -45,
        content:
          '<font style="color: rgba(220, 38, 38, 0.37); font-size: 60px">CONFIDENTIAL</font>',
        fontSize: 52,
        fontFamily: 'Arial, sans-serif',
        textAlign: 'center',
        lineHeight: 1.2,
        color: 'rgba(220,38,38,0.3)',
        type: 'watermark',
        id: 'watermark-singleton',
      },
      {
        id: 'container-1784331976934-1',
        x: 15,
        y: 1023,
        width: 799,
        height: 33,
        rotation: 0,
        type: 'container',
        backgroundColor: 'rgba(82,84,122,0.14)',
        borderLinked: true,
        borderTop: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderRight: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderBottom: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        borderLeft: {
          width: 0,
          style: 'solid',
          color: '#52547a',
        },
        radiusLinked: true,
        radiusTL: 4,
        radiusTR: 4,
        radiusBR: 4,
        radiusBL: 4,
        padding: 8,
      },
      {
        id: 'text-1784331986039-2',
        x: 292,
        y: 1026,
        width: 200,
        height: 30,
        rotation: 0,
        type: 'text',
        content: '<div style="text-align: center">{{agency.url}}</div>',
        fontSize: 16,
        fontFamily: 'Arial, sans-serif',
        textAlign: 'left',
        lineHeight: 1.25,
        color: '#333333',
      },
      {
        id: 'container-1784664665313-1',
        x: 0,
        y: 0,
        width: 20,
        height: 1056,
        rotation: 0,
        type: 'container',
        backgroundColor: '#52547a',
        borderLinked: true,
        borderTop: {
          width: 1,
          style: 'solid',
          color: '#52547a',
        },
        borderRight: {
          width: 1,
          style: 'solid',
          color: '#52547a',
        },
        borderBottom: {
          width: 1,
          style: 'solid',
          color: '#52547a',
        },
        borderLeft: {
          width: 1,
          style: 'solid',
          color: '#52547a',
        },
        radiusLinked: true,
        radiusTL: 0,
        radiusTR: 0,
        radiusBR: 0,
        radiusBL: 0,
        padding: 8,
      },
    ],
  },
}
